import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { AccountBalanceService } from 'src/services/account-balance.service';
import { UserContextService } from 'src/services/user-context.service';
import { AccountBalance } from 'src/app/core/models/billing/billing.model';

/**
 * Dashboard Resolver
 *
 * Pre-loads dashboard data to prevent FOUC on dashboard routes (activity, compliance, etc.)
 * Loads account balance and other common dashboard data before route activates.
 */
export interface DashboardData {
    accountBalance: AccountBalance | null;
    hasAgencyCapabilities: boolean;
    isFirmAccount: boolean;
    error?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DashboardResolverService implements Resolve<DashboardData> {

    constructor(
        private accountBalanceService: AccountBalanceService,
        private userContextService: UserContextService
    ) {}

    resolve(): Observable<DashboardData> {
        // Determine account capabilities synchronously (no HTTP call needed)
        const hasAgencyCapabilities = this.userContextService.isAuthorizedForFunction('agencyCapabilities');
        const isFirmAccount = this.userContextService.isFirmCapabilitiesAccount();

        // Load account balance if user has agency capabilities
        const accountBalance$ = hasAgencyCapabilities
            ? this.accountBalanceService.getAccountBalance().pipe(
                catchError(() => {
                    return of(null);
                })
            )
            : of(null);

        return accountBalance$.pipe(
            map((accountBalance) => {
                return {
                    accountBalance,
                    hasAgencyCapabilities,
                    isFirmAccount,
                    error: false
                };
            }),
            catchError(() => {
                return of({
                    accountBalance: null,
                    hasAgencyCapabilities,
                    isFirmAccount,
                    error: true
                });
            })
        );
    }
}
